import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Select, { BaseOptionType } from 'antd/lib/select';

import { isPresent, Nullable } from '@common/typescript/objects/Nullable';

import { Price } from '@app/objects/Price';
import { DeliveryTypeEntity } from '@app/objects/DeliveryType';
import { useDeliveryTypes } from '@app/hooks/Delivery/useDeliveryTypes';

interface OwnProps {
	withFieldName?: boolean;
	value: Nullable<number> | undefined;
	onChange: (value: Nullable<number>) => void;
	multiple?: boolean;
	onDeselect?: (fieldName: string, type: string, id?: number) => void;
	className?: string;
	selectClassName?: (value: string | Nullable<number> | undefined, length: number) => string;

	prices?: Array<Price> | false;
	crematoryId: number;

	disabled?: boolean;
}

const deliveryTypeKey = 'delivery-type';

function filterByQuery(list: Array<DeliveryTypeEntity>, query: string): Array<DeliveryTypeEntity> {
	if (!query) return list;

	const text = query.toLowerCase();

	return list.filter((item: DeliveryTypeEntity) => item.name.toLowerCase().includes(text));
}

function toPricesMap(prices?: Array<Price> | false): Map<number, Price> | false {
	if (prices === false) return false;
	if (!prices) return new Map<number, Price>();

	if (prices.some((p: Price) => p.clinicId !== null)) {
		prices = prices.filter((p: Price) => p.clinicId !== null);
	}

	return prices
		.reduce((acc: Map<number, Price>, cur: Price) => {
			if (!cur.deliveryEntityId) return acc;

			acc.set(cur.deliveryEntityId, cur);

			return acc;
		}, new Map<number, Price>());
}

function toOption(item: DeliveryTypeEntity, map: Map<number, Price> | false): Nullable<BaseOptionType> {
	if (map) {
		const price = map.get(item.id);
		if (price === undefined) return null;

		const cost = price.value === 0 ? 'Free' : `$${price.value.toFixed(0)}`;

		return {
			value: item.id,
			label: `${item.name} (${cost})`,
		};
	}

	return {
		value: item.id,
		label: item.name,
	};
}

export const DeliveryEntitySelect: React.FC<OwnProps> = (props: OwnProps) => {
	const { t } = useTranslation();
	const translator = React.useCallback((key: string) => t(`filters.${key}`), []);

	const [query, setQuery] = React.useState<string>('');
	const delivery = useDeliveryTypes();

	const options = React.useMemo<Array<BaseOptionType>>(() => {
		if (!delivery.list.length) return [];

		const list = filterByQuery(delivery.list, query);
		const map = toPricesMap(props.prices);

		return list
			.map((item: DeliveryTypeEntity) => toOption(item, map))
			.filter(isPresent);
	}, [delivery.list, props.prices, query]);

	return (
		<div className={props.className}>
			{props.withFieldName && <span className="field-name">{translator(deliveryTypeKey)}</span>}
			<span className="ant-select-selection__placeholder">{translator(deliveryTypeKey)}</span>
			<Select
				className={props.selectClassName ? props.selectClassName(props.value, query.length) : ''}
				showSearch
				showArrow
				allowClear
				loading={delivery.loading}
				onDeselect={(value: Nullable<number>) => props.onDeselect && props.onDeselect('deliveryTypeEntity', 'id', value || 0)}
				mode={props.multiple ? 'multiple' : undefined}
				showAction={['focus', 'click']}
				optionFilterProp="children"
				placeholder={translator(deliveryTypeKey)}
				value={props.value}
				onChange={(value: Nullable<number>) => props.onChange(typeof value === 'undefined' ? null : value)}
				onSearch={(query: string) => setQuery(query)}
				onSelect={() => setQuery('')}
				onBlur={() => setQuery('')}
				options={options}
				disabled={props.disabled}
			/>
		</div>
	);
};
